import { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { CollapsibleLinks } from '~/eds/src/components/CollapsibleLinks/CollapsibleLinks';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Progress } from '~/eds/src/components/Progress/Progress';
import { Text } from '~/eds/src/components/Text/Text';
import { StatusType } from '~/eds/src/types/StatusType';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { slices } from '~/redux/slices';

import { FieldModel } from '../../fields/types';
import { testIsTestCaseEvaluating } from '../../fields/utils';

interface OutcomeConfigs {
  [outcome: string /* i.e. TestCaseOutcome */]: {
    label: string;
    status: StatusType;
  };
}

interface Stat {
  label: string;
  status: StatusType;
  testCases: FieldModel['testCases'];
}

interface Props {
  testCases: FieldModel['testCases'];
  onUpdateTestCaseNumber: (testCaseNumber: number) => void;
  hideNavLinks?: boolean;
}

export const ReviewSummary = memo(
  ({ testCases, hideNavLinks = false, onUpdateTestCaseNumber }: Props) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const formatNumber = useFormatNumber();
    const activeModel = useSelector(slices.xRay.selectors.selectActiveModel);

    const isTestCasesEvaluating = testCases.some(testIsTestCaseEvaluating);

    const hasPartiallyCorrectOutcomes =
      activeModel?.config.fieldClassification?.isMulti;

    const stats: Stat[] = useMemo(() => {
      const outcomeConfigs: OutcomeConfigs = {
        correct: {
          label: intl.formatMessage({
            id: 'ReviewSummary.correct',
            defaultMessage: 'Correct',
            description: 'Label for correct test case outcomes',
          }),
          status: 'success',
        },
        ...(hasPartiallyCorrectOutcomes
          ? {
              partially_correct: {
                label: intl.formatMessage({
                  id: 'ReviewSummary.partiallyCorrect',
                  defaultMessage: 'Partially Correct',
                  description: 'Label for partially correct test case outcomes',
                }),
                status: 'warning',
              },
            }
          : {}),
        incorrect: {
          label: intl.formatMessage({
            id: 'ReviewSummary.incorrect',
            defaultMessage: 'Incorrect',
            description: 'Label for incorrect test case outcomes',
          }),
          status: 'danger',
        },
        skipped: {
          label: intl.formatMessage({
            id: 'ReviewSummary.skipped',
            defaultMessage: 'Skipped',
            description: 'Label for skipped test case outcomes',
          }),
          status: 'inactive',
        },
      };
      return Object.entries(outcomeConfigs).map(([outcome, config]) => ({
        ...config,
        testCases: testCases.filter((testCase) => testCase.outcome === outcome),
      }));
    }, [testCases]);
    const reviewedTestCaseCount = testCases.filter(
      ({ outcome }) =>
        outcome === 'correct' ||
        outcome === 'incorrect' ||
        outcome === 'partially_correct',
    ).length;

    const summaryContent = (stat: Stat, pt?: number) => (
      <Layout
        align="center"
        justify="space-between"
        spacing={2}
        w="100%"
        pt={pt}
      >
        <Text variant="body-bold" w="60px">
          {stat.label}
        </Text>
        <Progress
          isLoading={isTestCasesEvaluating}
          max={reviewedTestCaseCount}
          name={`review test cases (${stat.status})`}
          status={stat.status}
          statsLabel={{
            formatLabel: (value, max) =>
              stat.status === 'inactive'
                ? formatNumber(value)
                : `${formatNumber(value / max || 0, {
                    decimal: 1,
                    style: 'percent',
                  })} (${formatNumber(value)} of ${formatNumber(max)})`,
            textAlign: 'right',
            position: 'left',
            width: '120px',
          }}
          tooltip={
            isTestCasesEvaluating
              ? intl.formatMessage({
                  id: 'ReviewSummary.evaluatingResults',
                  defaultMessage: 'Evaluating results…',
                  description:
                    'Tooltip shown when test cases are being evaluated',
                })
              : undefined
          }
          value={stat.testCases.length}
        />
      </Layout>
    );

    const summaries = stats.map((stat) => summaryContent(stat, 2));

    const items = stats.map((stat) => ({
      content: (
        <Layout wrap spacing={1}>
          <Text flex="none" variant="body-medium">
            {intl.formatMessage({
              id: 'ReviewSummary.documentNumber',
              defaultMessage: 'Document No.:',
              description: 'Label for document number list',
            })}
          </Text>
          <CollapsibleLinks
            limit={20}
            links={stat.testCases.map((testCase) => ({
              text: String(testCase.number),
              tooltip: intl.formatMessage({
                id: 'ReviewSummary.viewDocument',
                defaultMessage: 'View document',
                description: 'Tooltip for document link',
              }),
              onClick: () => {
                const documentVersionId = Number(testCase.entity.id);
                onUpdateTestCaseNumber(testCase.number);
                dispatch(
                  slices.xRay.actions.setDocumentViewer({
                    documentVersionId,
                  }),
                );
              },
            }))}
          />
        </Layout>
      ),
      title: stat.label,
      summary: summaryContent(stat),
    }));
    return (
      <>
        {hideNavLinks ? (
          <>{summaries}</>
        ) : (
          <Accordion isIndented border="none" items={items} />
        )}
      </>
    );
  },
);
